/**
 * OnboardingStep5.tsx - Notifications & Study Preferences
 * Fifth and final step of the onboarding flow.
 * Collects:
 * - Assignment reminders (toggle)
 * - Preferred study time (single-select chips)
 * - Check-in frequency (single-select chips)
 * - SMS verification with phone number and 6-digit code
 * - SMS consent checkbox (only after verification)
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { View, TextInput } from "react-native";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon } from "@phosphor-icons/react";
import OnboardingStepHeader from "./common/OnboardingStepHeader";
import OnboardingFormSection from "./common/OnboardingFormSection";
import PreferenceRow from "./common/PreferenceRow";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import ChipSelector, { ChipOption } from "../../common/form/ChipSelect/ChipSelector";
import DigitCodeInput from "../../common/form/DigitCodeInput/DigitCodeInput";
import { colors, spacing, spacingStyles, radii } from "../../../styles/design-system";
import { sendSMSOTP, verifyReminderPhone } from "../../../services";
import { getValidAccessToken } from "../../../utils/authRefresh";

// Format phone number to (xxx)xxx-xxxx format
const formatPhoneNumber = (input: string): string => {
  const digits = input.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Validate phone number (must be 10 digits)
const isValidPhoneNumber = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, "");
  return digits.length === 10;
};

// Validate 6-digit code
const isValidCode = (code: string): boolean => {
  const digits = code.replace(/\D/g, "");
  return digits.length === 6;
};

export interface OnboardingStep5Props {
  assignmentReminders: boolean;
  onAssignmentRemindersChange: (value: boolean) => void;
  studyTime: string;
  onStudyTimeChange: (value: string) => void;
  checkInFrequency: string;
  onCheckInFrequencyChange: (value: string) => void;
  wantTextReminders: boolean;
  onWantTextRemindersChange: (value: boolean) => void;
  onPhoneVerifiedChange?: (verified: boolean) => void;
  onSmsOptInChange?: (optIn: boolean) => void;
}

function OnboardingStep5({
  assignmentReminders,
  onAssignmentRemindersChange,
  studyTime,
  onStudyTimeChange,
  checkInFrequency,
  onCheckInFrequencyChange,
  wantTextReminders,
  onWantTextRemindersChange,
  onPhoneVerifiedChange,
  onSmsOptInChange,
}: OnboardingStep5Props) {
  const { t } = useTranslation();

  // SMS verification state - managed locally (not part of form payload)
  const [phoneNumber, setPhoneNumber] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verificationError, setVerificationError] = useState<string | undefined>();
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const codeInputRef = useRef<TextInput>(null);

  // Reset verification state when text reminders is toggled off
  useEffect(() => {
    if (!wantTextReminders) {
      setPhoneNumber("");
      setCodeSent(false);
      setVerificationCode("");
      setIsVerified(false);
      setSmsOptIn(false);
      onPhoneVerifiedChange?.(false);
      onSmsOptInChange?.(false);
    }
  }, [wantTextReminders, onPhoneVerifiedChange, onSmsOptInChange]);

  // Auto-verify when 6 digits are entered
  useEffect(() => {
    if (verificationCode.length === 6 && !verifyingCode && !isVerified) {
      handleVerifyCode();
    }
  }, [verificationCode]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const studyTimeOptions: ChipOption[] = useMemo(
    () => [
      { key: "morning", value: t("text.companion_onboarding_study_time_morning") },
      { key: "afternoon", value: t("text.companion_onboarding_study_time_afternoon") },
      { key: "evening", value: t("text.companion_onboarding_study_time_evening") },
    ],
    [t]
  );

  const checkInFrequencyOptions: ChipOption[] = useMemo(
    () => [
      {
        key: "minimal",
        value: t("text.companion_onboarding_checkin_minimal"),
        subtitle: "Only urgent deadlines",
      },
      {
        key: "normal",
        value: t("text.companion_onboarding_checkin_normal"),
        subtitle: "Weekly check-ins + deadlines",
      },
      {
        key: "proactive",
        value: t("text.companion_onboarding_checkin_proactive"),
        subtitle: "Daily tips + study nudges",
      },
    ],
    [t]
  );

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
  };

  const handleSendCode = async () => {
    setVerificationError(undefined);
    if (!isValidPhoneNumber(phoneNumber)) {
      setVerificationError("Please enter a valid phone number");
      return;
    }

    setSendingCode(true);
    try {
      const token = await getValidAccessToken();
      // Extract country code (digits before the parentheses) and phone number (10 digits)
      const countryCode = "1"; // Default to US country code
      const digits = phoneNumber.replace(/\D/g, "");
      await sendSMSOTP(countryCode, digits, token);
      setCodeSent(true);
      setResendCountdown(46); // Start 46-second countdown
    } catch (err) {
      const errorMessage = err?.response?.data?.error || "Failed to send code. Please try again.";
      setVerificationError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerificationError(undefined);
    if (!isValidCode(verificationCode)) {
      setVerificationError("Please enter a valid 6-digit code");
      return;
    }

    setVerifyingCode(true);
    try {
      const token = await getValidAccessToken();
      // Extract country code and phone number
      const countryCode = "1"; // Default to US country code
      const digits = phoneNumber.replace(/\D/g, "");
      await verifyReminderPhone(countryCode, digits, verificationCode, token);
      setIsVerified(true);
      // Notify parent component that phone verification is complete
      onPhoneVerifiedChange?.(true);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || "Invalid code. Please try again.";
      setVerificationError(errorMessage);
      setVerificationCode(""); // Clear code on error
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleCodeChange = (value: string) => {
    // Only allow up to 6 digits
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(digits);
  };

  return (
    <View style={spacingStyles.mNone}>
      <OnboardingStepHeader
        titleKey="companion_onboarding_step5_title"
        subtitleKey="companion_onboarding_step5_subtitle"
      />

      <PreferenceRow
        titleKey="companion_onboarding_step5_assignment_reminders"
        descriptionKey="companion_onboarding_step5_assignment_reminders_desc"
        value={assignmentReminders}
        onValueChange={onAssignmentRemindersChange}
      />

      <View
        style={{
          borderRadius: radii.lg,
          backgroundColor: colors.grey[25],
          padding: spacing.md,
          marginBottom: spacing.md,
        }}>
        <OnboardingFormSection labelKey="companion_onboarding_step5_study_time_label" marginBottom={spacing.lg}>
          <ChipSelector
            options={studyTimeOptions}
            selectedKeys={studyTime ? [studyTime] : []}
            onSelectionChange={(keys) => onStudyTimeChange(keys[0] || "")}
            multiSelect={false}
          />
        </OnboardingFormSection>

        <OnboardingFormSection labelKey="companion_onboarding_step5_checkin_label" marginBottom={0}>
          <ChipSelector
            options={checkInFrequencyOptions}
            selectedKeys={checkInFrequency ? [checkInFrequency] : []}
            onSelectionChange={(keys) => onCheckInFrequencyChange(keys[0] || "")}
            multiSelect={false}
          />
        </OnboardingFormSection>
      </View>

      <PreferenceRow
        titleKey="companion_onboarding_step5_text_reminders"
        descriptionKey="companion_onboarding_step5_text_reminders_desc"
        value={wantTextReminders}
        onValueChange={onWantTextRemindersChange}
      />

      {wantTextReminders && (
        <>
          <View style={{ marginBottom: spacing.lg }}>
            <CustomText
              text="companion_onboarding_step5_phone_label"
              size="s"
              weight="medium"
              style={{ color: colors.grey[700], marginBottom: spacing.sm }}
            />
            <View
              style={{
                flexDirection: "row",
                gap: spacing.sm,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}>
              <View style={{ flex: 1, maxWidth: 240 }}>
                <CustomTextInput
                  value={phoneNumber}
                  onChangeText={handlePhoneChange}
                  placeholder="(123) 456-7890"
                  keyboardType="phone-pad"
                  editable={!isVerified}
                />
              </View>

              {!isVerified && (
                <CustomButton
                  text="Send code"
                  onPress={handleSendCode}
                  disabled={!isValidPhoneNumber(phoneNumber) || sendingCode}
                  styleType="light"
                  style={{
                    paddingHorizontal: spacing.lg,
                    backgroundColor: colors.blue[50],
                    borderWidth: 1,
                    borderColor: colors.blue[500],
                  }}
                  textStyle={{ color: colors.blue[700] }}
                />
              )}
            </View>

            {isVerified && (
              <View style={{ flexDirection: "row", alignItems: "center", gap: spacing.sm, marginTop: spacing.sm }}>
                <CheckCircleIcon size={20} color={colors.success[600]} weight="fill" />
                <CustomText
                  text="Verified"
                  size="s"
                  weight="medium"
                  style={{ color: colors.success[600] }}
                  useTranslationText={false}
                />
              </View>
            )}

            {verificationError && (
              <CustomText
                text={verificationError}
                size="s"
                style={{ color: colors.error[600], marginTop: spacing.sm }}
                useTranslationText={false}
              />
            )}
          </View>

          {codeSent && !isVerified && (
            <View style={{ marginBottom: spacing.lg }}>
              <CustomText
                text="Enter the 6-digit code sent to your phone"
                size="s"
                weight="medium"
                style={{ color: colors.grey[700], marginBottom: spacing.sm }}
                useTranslationText={false}
              />

              <View style={{ marginBottom: spacing.md }}>
                <DigitCodeInput
                  ref={codeInputRef}
                  value={verificationCode}
                  onChangeText={handleCodeChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  isFocused={isFocused}
                  autoFocus={true}
                />
              </View>

              <View style={{ alignItems: "center" }}>
                {verifyingCode ? (
                  <CustomText
                    text="Verifying..."
                    size="s"
                    style={{ color: colors.grey[600] }}
                    useTranslationText={false}
                  />
                ) : resendCountdown > 0 ? (
                  <CustomText
                    text={`Resend in 0:${resendCountdown.toString().padStart(2, "0")}`}
                    size="s"
                    style={{ color: colors.grey[600] }}
                    useTranslationText={false}
                  />
                ) : (
                  <CustomButton
                    text="Resend code"
                    onPress={handleSendCode}
                    disabled={sendingCode}
                    styleType="light"
                    style={{
                      paddingHorizontal: spacing.lg,
                      backgroundColor: colors.blue[50],
                      borderWidth: 1,
                      borderColor: colors.blue[500],
                    }}
                    textStyle={{ color: colors.blue[700] }}
                  />
                )}
              </View>
            </View>
          )}

          {isVerified && (
            <PreferenceRow
              titleKey="companion_onboarding_step5_sms_consent"
              value={smsOptIn}
              onValueChange={(value) => {
                setSmsOptIn(value);
                onSmsOptInChange?.(value);
              }}
              switchSize="m"
            />
          )}
        </>
      )}
    </View>
  );
}

export default OnboardingStep5;
